import {useCallback, useContext, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import {Avatar, Grid, IconButton, Typography} from '@mui/material';

import {CompanyProfile} from '../types';

import {CompanyProfileDialogButtons} from './CompanyProfileDialogButtons';
import {ConfirmDeleteDialog} from '../../../../components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import {SnackBarContext} from '../../../../contexts/SnackBarContext/SnackBarContext';
import {useChecksum} from '../../../../hooks/useChecksum';
import {useSecureS3Upload} from '../../../../hooks/useSecureS3Upload';
import {useUserAvatar} from '../../../../hooks/useUserAvatar';
import {AppConfig} from '../../../../lib/AppConfig';

type CompanyProfileHeaderLogoPropsType = {
  company: CompanyProfile;
  updateCompanyLogo: (logoId: string | null) => void;
};

export default function CompanyProfileHeaderLogo({company, updateCompanyLogo}: CompanyProfileHeaderLogoPropsType) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [newLogoFile, setNewLogoFile] = useState<File | null>(null);
  const {checksum, computeChecksum} = useChecksum();
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | undefined>();
  const {updateAvatarUrl} = useUserAvatar();

  useEffect(() => {
    setCompanyLogoUrl(company?.logoUrl);
  }, [company?.logoUrl]);

  const [imageRemoveDialogOpen, setImageRemoveDialogOpen] = useState(false);
  const {dispatchMessage} = useContext(SnackBarContext);

  const {t} = useTranslation();

  const handleImageRemoveClick = () => {
    setImageRemoveDialogOpen(true);
  };

  const handleImageRemoveCancel = () => {
    setImageRemoveDialogOpen(false);
  };

  const cancelChanges = () => {
    setNewLogoFile(null);
    setCompanyLogoUrl(company?.logoUrl);
  };

  const onChange = (e: any) => {
    const file = e.target.files[0];
    if (file) {
      const fileSizeInBytes = file.size;
      const maxSizeInBytes = AppConfig.APPLICANT_PROFILE_PICTURE_MAX_SIZE_IN_BYTES;
      if (fileSizeInBytes <= maxSizeInBytes) {
        setErrorMessage(null);
        setNewLogoFile(file);
        setCompanyLogoUrl(URL.createObjectURL(file));
        computeChecksum(file);
      } else {
        setErrorMessage(t('companyProfile.header.media.logo.maxFileSizeLimit', {maxSize: 1}));
      }
    }
  };

  const {
    makeRequest,
    isLoading,
    error: errorUploading,
  } = useSecureS3Upload({
    scope: 'companies',
    ownerId: company.resourceId,
    objectType: 'logo',
    file: newLogoFile,
    checksum: checksum,
    onSuccess: async (_response, signedId) => {
      if (signedId) {
        updateCompanyLogo(signedId);
        setNewLogoFile(null);
      }
    },
  });

  useEffect(() => {
    if (errorUploading) {
      const message = t('companyProfile.header.media.errorHandling.uploadError');
      dispatchMessage(message, 'error');
    }
  }, [errorUploading, t, dispatchMessage]);

  const onDelete = useCallback(() => {
    setCompanyLogoUrl('');
    updateAvatarUrl('');
    updateCompanyLogo(null);
    setImageRemoveDialogOpen(false);
  }, [updateCompanyLogo, updateAvatarUrl]);

  return (
    <>
      <Grid item xs={12} display={'flex'} justifyContent={'center'}>
        <Avatar
          aria-label='company logo'
          variant='square'
          src={companyLogoUrl ?? undefined}
          sx={{width: 200, height: 200, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}
        >
          <Typography>{t('companyProfile.header.media.logo.noImageText')}</Typography>
          <Typography>{t('companyProfile.header.media.logo.recommendedSize')}</Typography>
        </Avatar>
      </Grid>
      <Grid item xs={12} display={'flex'} justifyContent={'center'} mt={1}>
        <input accept='image/*' id='header-logo-upload' type='file' style={{display: 'none'}} onChange={onChange} />
        <label htmlFor='header-logo-upload'>
          <IconButton aria-label='upload picture' component='span'>
            <PhotoCameraIcon />
          </IconButton>
        </label>
        <label htmlFor='delete-picture-button'>
          <IconButton
            aria-label='delete picture'
            component='span'
            disabled={!companyLogoUrl}
            onClick={handleImageRemoveClick}
          >
            <DeleteIcon />
          </IconButton>
        </label>
      </Grid>
      <Grid item xs={12} display={'flex'} justifyContent={'center'}>
        {newLogoFile && (
          <CompanyProfileDialogButtons onSubmit={makeRequest} onCancel={cancelChanges} isLoading={isLoading} />
        )}
      </Grid>
      {errorMessage && (
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography sx={{fontSize: 14}} color='error.main' gutterBottom align='center'>
            {errorMessage}
          </Typography>
        </Grid>
      )}
      <ConfirmDeleteDialog
        open={imageRemoveDialogOpen}
        aria-labelledby='logo-remove-alert-dialog-title'
        aria-describedby='logo-remove-alert-dialog-description'
        dialogContent={t('companyProfile.header.media.logo.removeConfirmation')}
        handleConfirm={onDelete}
        handleCancel={handleImageRemoveCancel}
      />
    </>
  );
}
